import React, { useEffect, useState } from "react";
import GoodsReceivedPDF from "../../../../../pdf/GoodsReceivedPDF";
import { Card, CardTitle, Alert } from "reactstrap";
import { Col, Row, Icon, Button } from "../../../../../../../../components/Component";
import * as app_consts from "../../../../../../../../constants/react_app_consts";
import { PDFViewer } from "@react-pdf/renderer";
import { resolveWeighbridgeTicketPrefillData } from "../../../../../../../../utils/weighbridgeTicketNormalization";
import { mapWeighbridgeWeightsToGrn } from "../../../../../../../../utils/weighbridgeUtils";

const GoodsReceivedTicketStep = ({
  weighBridgeFormData,
  setWeighBridgeFormData,
  continue_processing_data,
  setContinueProcessingData,
  prevStep,
  nextStep,
  selectedWeighbridgeTicket,
}) => {
  const [page_data, setPageData] = useState(continue_processing_data);

  useEffect(() => {
    setPageData(continue_processing_data);
  }, [continue_processing_data]);

  // ------------------------------------------------------
  // SAFE SYNC OF LINKED WEIGHBRIDGE TICKET INTO FORM STATE
  // ------------------------------------------------------
  useEffect(() => {
    if (!selectedWeighbridgeTicket) return;

    const selectedTicketPrefill =
      resolveWeighbridgeTicketPrefillData(selectedWeighbridgeTicket);
    const grnWeights = mapWeighbridgeWeightsToGrn(selectedTicketPrefill);

    const selectedTicketId =
      selectedTicketPrefill?.weighbridge_ticket_id ||
      selectedWeighbridgeTicket?.weighbridge_ticket_id ||
      selectedWeighbridgeTicket?.weigh_bridge_ticket_id ||
      selectedWeighbridgeTicket?.id ||
      "";

    setWeighBridgeFormData((prev) => ({
      ...prev,
      weighbridge_ticket_id:
        prev?.weighbridge_ticket_id || selectedTicketId,
      weigh_bridge_ticket_number:
        prev?.weigh_bridge_ticket_number ||
        selectedTicketPrefill?.weigh_bridge_ticket_number ||
        "",
      weigh_bridge_ticket_reference_no:
        prev?.weigh_bridge_ticket_reference_no ||
        selectedTicketPrefill?.weigh_bridge_ticket_reference_no ||
        "",
      weighbridge_ticket_data:
        prev?.weighbridge_ticket_data ||
        selectedTicketPrefill?.weighbridge_ticket_data ||
        selectedWeighbridgeTicket ||
        null,
      truck_driver_name:
        prev?.truck_driver_name || selectedTicketPrefill?.truck_driver_name || "",
      transporter_name:
        prev?.transporter_name || selectedTicketPrefill?.transporter_name || "",
      truck_licence_plate:
        prev?.truck_licence_plate || selectedTicketPrefill?.truck_licence_plate || "",
      first_weight:
        prev?.first_weight || grnWeights.first_weight || "",
      second_weight:
        prev?.second_weight || grnWeights.second_weight || "",
      net_weight:
        prev?.net_weight ||
        grnWeights.net_weight ||
        "",
      weighbridge_locked: true,
    }));
  }, [selectedWeighbridgeTicket, setWeighBridgeFormData]);

  // ------------------------------------------------------
  // MERGE WEIGHBRIDGE DATA INTO PDF PAGE DATA
  // ------------------------------------------------------
  useEffect(() => {
    setPageData((prev) => ({
      ...(prev || {}),
      weighbridge_ticket_id: weighBridgeFormData?.weighbridge_ticket_id || "",
      weigh_bridge_ticket_number:
        weighBridgeFormData?.weigh_bridge_ticket_number || "",
      weigh_bridge_ticket_reference_no:
        weighBridgeFormData?.weigh_bridge_ticket_reference_no || "",
      truck_driver_name: weighBridgeFormData?.truck_driver_name || "",
      transporter_name: weighBridgeFormData?.transporter_name || "",
      truck_licence_plate: weighBridgeFormData?.truck_licence_plate || "",
      first_weight: weighBridgeFormData?.first_weight || "",
      second_weight: weighBridgeFormData?.second_weight || "",
      net_weight: weighBridgeFormData?.net_weight || "",
      weighbridge_ticket_data: weighBridgeFormData?.weighbridge_ticket_data || null,
    }));
  }, [weighBridgeFormData]);

  return (
    <>
      <Card className="card-inner">
        <CardTitle tag="h5">Goods Received Note</CardTitle>

        <Col xl="12" className="mb-3">
          <div className="form-group">
            <div className="form-control-wrap-flex-wrap justify-start">
              <Button
                onClick={prevStep}
                className="btn btn-dim btn-outline-primary"
                size="md"
              >
                Go to Previous Step
              </Button>
            </div>
          </div>
        </Col>

        <Col lg="12">
          <div className="form-group">
            {weighBridgeFormData.consigner_type_id === app_consts.UAG_CONSIGNER_TYPE ? (
              <>
                <div className="form-label-group">
                  <label className="form-label text-soft">User Account ID :</label>
                </div>
                <div className="form-control-wrap">
                  <label className="form-label text-primary">UAG</label>
                </div>
              </>
            ) : (
              <>
                <div className="form-label-group">
                  <label className="form-label text-soft">User Account ID :</label>
                </div>
                <div className="form-control-wrap">
                  <label className="form-label text-primary">
                    {weighBridgeFormData.user_account_label}
                  </label>
                </div>
              </>
            )}
          </div>
        </Col>

        {weighBridgeFormData?.weighbridge_ticket_id && (
          <Alert color="light" className="border">
            <div className="fw-bold mb-2">
              <Icon name="link" className="me-1" />
              Linked Weighbridge Ticket
            </div>
            <div>
              <b>Ticket No:</b>{" "}
              {weighBridgeFormData?.weigh_bridge_ticket_number || "--"}
            </div>
            <div>
              <b>Reference No:</b>{" "}
              {weighBridgeFormData?.weigh_bridge_ticket_reference_no || "--"}
            </div>
            <div>
              <b>Truck Plate:</b>{" "}
              {weighBridgeFormData?.truck_licence_plate || "--"}
            </div>
            <div>
              <b>Driver:</b>{" "}
              {weighBridgeFormData?.truck_driver_name || "--"}
            </div>
            <div>
              <b>Transporter:</b>{" "}
              {weighBridgeFormData?.transporter_name || "--"}
            </div>
            <div>
              <b>Gross Weight:</b>{" "}
              {weighBridgeFormData?.first_weight || "--"}
            </div>
            <div>
              <b>Tare Weight:</b>{" "}
              {weighBridgeFormData?.second_weight || "--"}
            </div>
            <div>
              <b>Net Weight:</b>{" "}
              {weighBridgeFormData?.net_weight || "--"}
            </div>
          </Alert>
        )}

        <Row className="g-4">
          <div
            style={{ height: "90vh", width: "100%" }}
            className="border-2 border-text-gray-400"
          >
            <PDFViewer height="100%" width="100%" showToolbar>
              <GoodsReceivedPDF page_data={page_data} />
            </PDFViewer>
          </div>
        </Row>
      </Card>
    </>
  );
};

export default GoodsReceivedTicketStep;
